/**
 * Utility functions for dashboard routing and navigation
 */

export type PrimaryRole = 'superadmin' | 'admin' | 'landlord' | 'tenant'

const LANDLORD_ROLE_NAMES = new Set([
	'landlord',
	'tenant_owner',
	'property_manager',
	'leasing_agent',
	'viewer',
])

/**
 * Resolve the user's effective primary role from their assigned role names and userType.
 * Any landlord-side role (tenant_owner, property_manager, etc.) collapses to
 * 'landlord' so dashboard rendering and nav selection stay consistent.
 * For custom landlord-side roles, userType='landlord' provides a catch-all.
 */
export function getPrimaryRole(
	userRoles: string[],
	userType?: string,
): PrimaryRole {
	if (userRoles.includes('superadmin')) return 'superadmin'
	if (userRoles.includes('admin')) return 'admin'
	if (userRoles.some((r) => LANDLORD_ROLE_NAMES.has(r))) return 'landlord'
	if (userType === 'landlord') return 'landlord'
	return 'tenant'
}

/**
 * Get the correct dashboard URL based on user roles
 * @param userRoles - Array of user roles
 * @returns The appropriate dashboard URL
 */
export function getDashboardUrl(userRoles: string[]): string {
	// Determine primary role (highest priority)
	if (userRoles.includes('superadmin')) {
		return '/dashboard' // SuperAdmin uses unified dashboard
	}
	if (userRoles.includes('admin')) {
		return '/dashboard' // Admin uses unified dashboard
	}
	if (userRoles.includes('landlord')) {
		return '/dashboard' // Landlord uses unified dashboard
	}
	// Default to tenant
	return '/dashboard' // Tenant uses unified dashboard
}

/**
 * Get the correct dashboard title based on user roles
 * @param userRoles - Array of user roles
 * @returns The appropriate dashboard title
 */
export function getDashboardTitle(userRoles: string[]): string {
	// Determine primary role (highest priority)
	if (userRoles.includes('superadmin')) {
		return 'Super Admin Dashboard'
	}
	if (userRoles.includes('admin')) {
		return 'Admin Dashboard'
	}
	if (userRoles.includes('landlord')) {
		return 'Landlord Dashboard'
	}
	// Default to tenant
	return 'Tenant Dashboard'
}

/**
 * Check if user has access to a specific dashboard feature
 * @param userRoles - Array of user roles
 * @param userPermissions - Array of user permissions
 * @param requiredPermission - Required permission for the feature
 * @returns Whether user has access
 */
export function hasDashboardAccess(
	userRoles: string[],
	userPermissions: string[],
	requiredPermission: string,
): boolean {
	// SuperAdmin has access to everything
	if (userRoles.includes('superadmin')) {
		return true
	}

	// Check if user has the required permission
	return (
		userPermissions.includes(requiredPermission) ||
		userPermissions.includes('full_access')
	)
}

/**
 * Get role-specific dashboard data structure
 * @param userRoles - Array of user roles
 * @returns Object with role-specific data keys
 */
export function getDashboardDataKeys(userRoles: string[]): string[] {
	// Determine primary role (highest priority)
	if (userRoles.includes('superadmin')) {
		return ['superadmin']
	}
	if (userRoles.includes('admin')) {
		return ['admin']
	}
	if (userRoles.includes('landlord')) {
		return ['landlord']
	}
	// Default to tenant
	return ['tenant']
}
